import { ensurePassportSdk } from './passport';

import { withReadyPassport } from '@/utils/waitPassportSdk';

export type CheckPhoneResponseSuccess = {
    url: string;
    response: {
        errno: number;
        error: string;
        requestid: string;
        traceid: string;
        time: string;
        code_type: number;
        prompt: string;
        data: {
            code_len: number;
            code_tag: string;
        };
        support_voice: boolean;
    };
};

export type PassportResponseError = {
    url?: string;
    response?: {
        errno: number;
        error: string;
        requestid?: string;
        traceid?: string;
        time?: string;
    };
    error?: unknown;
};

export type CheckPhoneResponseError = PassportResponseError;

export const checkPhoneApi = (phone: string) =>
    withReadyPassport(
        () =>
            new Promise<CheckPhoneResponseSuccess>((resolve, reject) => {
                try {
                    ensurePassportSdk();
                } catch (error) {
                    reject(error);

                    return;
                }

                window.login.checkPhoneApi(
                    phone,
                    (response: CheckPhoneResponseSuccess) => {
                        resolve(response);
                    },
                    (error: CheckPhoneResponseError) => {
                        reject(error);
                    }
                );
            })
    );

export type GenCaptchaResponseSuccess = {
    url: string;
    response: {
        errno: number;
        error: string;
        requestid: string;
        traceid: string;
        time: string;
        captcha: string;
    };
};

export type GenCaptchaResponseError = CheckPhoneResponseError;

export const genCaptchaApi = () =>
    withReadyPassport(
        () =>
            new Promise<GenCaptchaResponseSuccess>((resolve, reject) => {
                try {
                    ensurePassportSdk();
                } catch (error) {
                    reject(error);

                    return;
                }

                window.login.genCaptchaApi(
                    (response: GenCaptchaResponseSuccess) => {
                        resolve(response);
                    },
                    (error: GenCaptchaResponseError) => {
                        reject(error);
                    }
                );
            })
    );

export type VerifyCaptchaResponseSuccess = {
    url: string;
    response: {
        errno: number;
        error: string;
        requestid: string;
        traceid: string;
        time: string;
    };
};

export const verifyCaptchaApi = (code: string) =>
    withReadyPassport(
        () =>
            new Promise<VerifyCaptchaResponseSuccess>((resolve, reject) => {
                try {
                    ensurePassportSdk();
                } catch (error) {
                    reject(error);

                    return;
                }

                window.login.verifyCaptchaApi(
                    code,
                    (response: VerifyCaptchaResponseSuccess) => {
                        resolve(response);
                    },
                    (error: CheckPhoneResponseError) => {
                        reject(error);
                    }
                );
            })
    );

export type SignInByCodeResponseSuccess = {
    url: string;
    response: {
        errno: number;
        error: string;
        requestid: string;
        traceid: string;
        time: string;
        ticket: string;
        uid: string;
        cell: string;
        uid_str: string;
        suid: string;
    };
};

export const signInByCodeApi = (code: string, inputTime: number) =>
    withReadyPassport(
        () =>
            new Promise<SignInByCodeResponseSuccess>((resolve, reject) => {
                try {
                    ensurePassportSdk();
                } catch (error) {
                    reject(error);

                    return;
                }

                window.login.signInByCodeApi(
                    code,
                    inputTime,
                    (response: SignInByCodeResponseSuccess) => {
                        resolve(response);
                    },
                    (error: CheckPhoneResponseError) => {
                        reject(error);
                    }
                );
            })
    );

type CaptchaEventType =
    | 'ready'
    | 'initFail'
    | 'verifyFail'
    | 'success'
    | 'close'
    | 'resendFail';

interface CaptchaEventParams<T = unknown> {
    event: CaptchaEventType;
    type: string;
    payload: T;
}

interface SliderCaptchaInstance {
    type: string;
    destroy: () => void;
    getInstance: () => unknown;
    onEvent: (callback: (params: CaptchaEventParams) => void) => void;
}

interface SliderCaptchaEventHandlers {
    onReady?: (type: string, payload: unknown) => void;
    onInitFail?: (type: string, payload: unknown) => void;
    onVerifyFail?: (type: string, payload: unknown) => void;
    onSuccess?: (type: string, payload: unknown) => void;
    onClose?: (type: string, payload: unknown) => void;
    onResendFail?: (type: string, payload: unknown) => void;
}

interface SliderCaptchaController {
    type: string;
    destroy: () => void;
    getInstance: () => unknown;
}

export const genSliderCaptchaApi = (
    handlers: SliderCaptchaEventHandlers = {}
): Promise<SliderCaptchaController> =>
    withReadyPassport(
        () =>
            new Promise<SliderCaptchaController>((resolve, reject) => {
                try {
                    ensurePassportSdk();
                } catch (error) {
                    reject(error);

                    return;
                }

                window.login.genSliderCaptchaApi(
                    (instance: SliderCaptchaInstance) => {
                        const {
                            onReady,
                            onInitFail,
                            onVerifyFail,
                            onSuccess,
                            onClose,
                            onResendFail
                        } = handlers;

                        instance.onEvent(({ event, type, payload }) => {
                            switch (event) {
                                case 'ready':
                                    onReady?.(type, payload);
                                    break;
                                case 'initFail':
                                    onInitFail?.(type, payload);
                                    break;
                                case 'verifyFail':
                                    onVerifyFail?.(type, payload);
                                    break;
                                case 'success':
                                    onSuccess?.(type, payload);
                                    break;
                                case 'close':
                                    onClose?.(type, payload);
                                    break;
                                case 'resendFail':
                                    onResendFail?.(type, payload);
                                    break;
                                default:
                                    break;
                            }
                        });

                        resolve({
                            type: instance.type,
                            destroy: instance.destroy,
                            getInstance: instance.getInstance
                        });
                    },
                    (error: unknown) => {
                        reject(error);
                    }
                );
            })
    );

export const getPassportError = (error: unknown) => {
    const passportError = error as CheckPhoneResponseError;

    return passportError?.response || null;
};
